import { memo, useCallback, useMemo, useEffect, useState } from 'react';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import CRMPropertyDateTimeInput from '../../../datetime/CRMPropertyDateTimeInput';
import { getDisplayedValue } from 'customer-data-property-utils/PropertyValueDisplay';
import { Button } from 'foundations-components/transitional/components/Button';
import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import { Small } from 'foundations-components/transitional/components/Small';
import { getTransferrableProps } from '../../../utils/getTransferrableProps';
import emptyFunction from 'react-utils/emptyFunction';
import { useObjectId } from '../../../v2/hooks/useObjectId';
import { usePropertyValidation } from '../../../validation/hooks/usePropertyValidation';
import { useObjectTypeId } from '../../../v2/hooks/useObjectTypeId';
import { useProperty } from '../../../v2/hooks/useProperty';
import { usePropertyValue } from '../../../v2/hooks/usePropertyValue';
import { syntheticEvent } from 'foundations-components/transitional/utils/SyntheticEvent';
import { Popover, PopoverBody, PopoverCloseButton } from 'foundations-components/transitional/components/Popover';
import TextInputAdapter from '../../../adapters/TextInput';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const DateTimePopover = props => {
  const {
    onFocus,
    popoverProps,
    popoverOpen,
    setPopoverOpen,
    disableValidation,
    applyChange,
    showAutoSaveText
  } = props;
  const objectId = useObjectId();
  const property = useProperty();
  const objectTypeId = useObjectTypeId();
  const value = usePropertyValue();
  const [draftValue, setDraftValue] = useState(value);
  const valueDoesNotExist = draftValue === undefined || draftValue == null || draftValue === '';
  const hasValueChanged = value !== draftValue;
  const handleDraftChange = useCallback(changes => {
    const newDatetimeValue = changes[property.name];
    setDraftValue(newDatetimeValue);
  }, [property.name]);
  const resetDraftState = useCallback(() => {
    setDraftValue(value);
  }, [value]);
  const {
    onChangeSingle,
    validationState
  } = usePropertyValidation({
    disableValidation,
    objectId,
    objectTypeId,
    onChange: handleDraftChange,
    // No `onValidationChange` needed because we don't broadcast the result of
    // this validation to our parent component. Validation is entirely internal
    // to this component
    onValidationChange: emptyFunction,
    property,
    propertyValue: draftValue,
    validationSettings: {
      validateInitialValue: true
    }
  });
  const saveable = disableValidation ? true : validationState.saveable;
  const isSaveEnabled = hasValueChanged && saveable;
  const handleChange = useCallback(evt => {
    const {
      target: {
        value: targetValue
      }
    } = evt;
    if (typeof onChangeSingle === 'function') {
      onChangeSingle(targetValue == null ? syntheticEvent(null) : syntheticEvent(String(targetValue)));
    }
  }, [onChangeSingle]);
  const closePopoverAndResetState = useCallback(() => {
    setPopoverOpen(false);
    resetDraftState();
  }, [resetDraftState, setPopoverOpen]);
  const saveAndClosePopover = useCallback(() => {
    applyChange(draftValue);
    setPopoverOpen(false);
  }, [applyChange, draftValue, setPopoverOpen]);
  useEffect(() => {
    if (!popoverOpen) {
      resetDraftState();
    }
  }, [popoverOpen, resetDraftState]);

  // We only show the timezone difference message if the user's timezone is different from the portal's timezone
  const userTzDiffersFromPortalTz = useMemo(() => I18n.moment.userTz().zoneName() !== I18n.moment.portalTz().zoneName(), []);
  const renderContent = useCallback(() => {
    return /*#__PURE__*/_jsxs(PopoverBody, {
      paddingBlockEnd: "500",
      children: [/*#__PURE__*/_jsxs(TFlex, {
        justify: "between",
        children: [/*#__PURE__*/_jsx("b", {
          children: property.label
        }), /*#__PURE__*/_jsx(PopoverCloseButton, {
          onClick: closePopoverAndResetState
        })]
      }), /*#__PURE__*/_jsxs(TFlex, {
        direction: "column",
        gap: 12,
        marginBlockStart: "200",
        children: [/*#__PURE__*/_jsxs(TFlex, {
          direction: "column",
          gap: 8,
          children: [/*#__PURE__*/_jsx(CRMPropertyDateTimeInput, Object.assign({}, props, {
            value: draftValue,
            onChange: handleChange,
            error: validationState.invalid
          })), /*#__PURE__*/_jsxs(TFlex, {
            direction: "column",
            gap: 4,
            children: [userTzDiffersFromPortalTz && validationState.invalid ? /*#__PURE__*/_jsx(Small, {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "customerDataProperties.PropertyInputDateTime.timezone",
                options: {
                  timezone: `${I18n.moment.userTz().format('UTC Z z')}`
                }
              })
            }) : null, validationState.invalid && /*#__PURE__*/_jsx(Small, {
              "data-test-id": "popover-validation-message",
              use: "error",
              children: validationState.message
            })]
          })]
        }), /*#__PURE__*/_jsxs(TFlex, {
          direction: "row",
          align: "center",
          children: [/*#__PURE__*/_jsx(Button, {
            use: "tertiary",
            size: "xs",
            onClick: saveAndClosePopover,
            disabled: !isSaveEnabled,
            children: showAutoSaveText ? /*#__PURE__*/_jsx(FormattedMessage, {
              message: "customerDataProperties.PropertyInputDateTime.save"
            }) : /*#__PURE__*/_jsx(FormattedMessage, {
              message: "customerDataProperties.PropertyInputDateTime.apply"
            })
          }), /*#__PURE__*/_jsx(Button, {
            use: "tertiary-light",
            size: "xs",
            onClick: closePopoverAndResetState,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "customerDataProperties.PropertyInputDateTime.cancel"
            })
          }), /*#__PURE__*/_jsx(Button, {
            use: "link",
            size: "xs",
            onClick: () => handleChange(syntheticEvent(null)),
            disabled: valueDoesNotExist,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "customerDataProperties.PropertyInputDateTime.clear"
            })
          })]
        })]
      })]
    });
  }, [property.label, closePopoverAndResetState, props, draftValue, handleChange, validationState.invalid, validationState.message, userTzDiffersFromPortalTz, saveAndClosePopover, isSaveEnabled, showAutoSaveText, valueDoesNotExist]);
  const handleOpenChange = useCallback(evt => {
    setPopoverOpen(evt.target.value);
  }, [setPopoverOpen]);
  const handleFocus = useCallback(evt => {
    if (onFocus) {
      onFocus(evt);
    }
    setPopoverOpen(true);
  }, [onFocus, setPopoverOpen]);
  const formattedValue = useMemo(() => getDisplayedValue(property, value, undefined, {
    showDateDisplayHintLabel: true
  }, objectTypeId), [objectTypeId, property, value]);
  return /*#__PURE__*/_jsx(Popover, Object.assign({
    inlineSize: "400px",
    placement: "right"
  }, popoverProps, {
    open: popoverOpen,
    content: renderContent(),
    onOpenChange: handleOpenChange,
    closeOnTargetLeave: true,
    "data-test-id": "property-input-date-time-popover",
    children: /*#__PURE__*/_jsx(TextInputAdapter, Object.assign({}, getTransferrableProps(props), {
      value: formattedValue,
      inputRef: props.inputRef,
      onChange: emptyFunction,
      onFocus: handleFocus,
      readOnly: props.readOnly,
      disabled: props.disabled,
      "data-test-id": "property-input-date-time-button"
    }))
  }));
};
export default /*#__PURE__*/memo(DateTimePopover);